import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import GlobalThrobberComponent from '../components/global_throbber_component'
import CarouselComponent from '../components/carousel_component'
import { useNotification } from '../components/global_noification_component'
import '../css/items_page.css'

// Helper to read a cookie by name
function getCookie(name) {
  const match = document.cookie.split('; ').find(row => row.startsWith(`${name}=`));
  return match ? match.split('=')[1] : '';
}

const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT

export default function ItemsPage() {
  // Thread expand/collapse state for comments
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const toggleExpand = (id) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Helper to recursively render comments and their replies
  const renderComments = (commentList, depth = 0) => {
    return commentList.map(c => (
      <React.Fragment key={c.comment_id}>
        <div className="zoom-comment-item" style={{ marginLeft: depth * 48 }}>
          <img
            src={c.pfp}
            alt={`${c.username}'s avatar`}
            className="comment-avatar"
          />
          <div className="comment-content">
            <span className="comment-username">{c.username}</span>
            <span className="comment-text">{c.comment_text}</span>
            <div className="comment-actions">
              {loggedIn && (
                <>
                  <button className="like-button" onClick={() => handleReaction(c.comment_id, 'like')}>
                    👍 {c.like_count || 0}
                  </button>
                  <button className="dislike-button" onClick={() => handleReaction(c.comment_id, 'dislike')}>
                    👎 {c.dislike_count || 0}
                  </button>
                  {depth === 0 && (
                    <button className="reply-button" onClick={() => setReplyToId(c.comment_id)}>
                      Reply
                    </button>
                  )}
                </>
              )}
            </div>
            {c.replies && c.replies.length > 0 && (
              <button
                className="expand-replies-button"
                onClick={() => toggleExpand(c.comment_id)}
              >
                {expandedThreads.has(c.comment_id)
                  ? 'Hide replies'
                  : `View replies (${c.replies.length})`}
              </button>
            )}
            {loggedIn && replyToId === c.comment_id && (
              <div className="reply-form">
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Reply..."
                />
                <button onClick={submitComment}>Post Reply</button>
                <button onClick={() => { setReplyToId(''); setNewComment(''); }}>Cancel</button>
              </div>
            )}
          </div>
        </div>
        {c.replies && c.replies.length > 0 && expandedThreads.has(c.comment_id) && renderComments(c.replies, depth + 1)}
      </React.Fragment>
    ));
  };
  const [items, setItems] = useState([])
  const user = useSelector(state => state.user);
  // Check login status via cookie
  const loggedIn = document.cookie
    .split(';')                    // split on semicolons
    .map(c => c.trim())            // trim whitespace around each cookie
    .some(cookie => {
      const [name, value] = cookie.split('=');
      return name === 'logged_in' && value === 'true';
    });
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [quickFilter, setQuickFilter] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [advPurchaseType, setAdvPurchaseType] = useState('')
  const [advAccessType, setAdvAccessType] = useState('')
  const [expandedBundles, setExpandedBundles] = useState({})
  const [selectedBundle, setSelectedBundle] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(24)
  const [searchDebounce, setSearchDebounce] = useState('')
  const [zoomItem, setZoomItem] = useState(null)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [replyToId, setReplyToId] = useState('');
  const [replyText, setReplyText] = useState('');
  const [gallery, setGallery] = useState([])
  const [galleryPage, setGalleryPage] = useState(1)
  const [gallerySort, setGallerySort] = useState('date')
  const [galleryHasMore, setGalleryHasMore] = useState(false)
  const [uploading, setUploading] = useState(false)
  const PURCHASE_OPTIONS = ['', 'Unlockable', 'Bundle', 'Coins', 'Purchase']
  const ACCESS_OPTIONS = ['', 'Public', 'role_base', 'pro_only']

  const fileInputRef = useRef(null)
  const [matchModalOpen, setMatchModalOpen] = useState(false)
  const [matchLoading, setMatchLoading] = useState(false)
  const [matchedItems, setMatchedItems] = useState([])
  const { itemId } = useParams();
  const location = useLocation();

let deepLinkId = itemId || new URLSearchParams(location.search).get('item_id');
if (!deepLinkId) {
  const m = window.location.hash.match(/#\/items\/([a-f0-9-]{36})/i);
  if (m) deepLinkId = m[1];
}

  const loadComments = async () => {
    try {
      const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${zoomItem.item_id}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(Array.isArray(data) ? data : []);
      } else {
        setComments([]);
      }
    } catch {
      setComments([]);
    }
  };
  useEffect(() => {
    if (!zoomItem) return;
    loadComments();
  }, [zoomItem]);

  useEffect(() => {
    if (!zoomItem) return
    setGallery([])
    setGalleryPage(1)
    setGallerySort('date')
    setGalleryHasMore(false)
  }, [zoomItem])

  useEffect(() => {
    if (!zoomItem) return
    const loadGallery = async () => {
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${zoomItem.item_id}/gallary`);
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data)
            ? data
            : (Array.isArray(data.items) ? data.items : []);
          const itemsMapped = items.map(it => ({ ...it, url: it.image_url, thumb: it.image_url }));
          setGallery(galleryPage === 1 ? itemsMapped : g => [...g, ...itemsMapped]);
          // Assume backend returns full list; no paging token yet
          setGalleryHasMore(false);
        } else {
          if (galleryPage === 1) setGallery([]);
          setGalleryHasMore(false);
        }
      } catch {
        if (galleryPage === 1) setGallery([]);
        setGalleryHasMore(false);
      }
    };
    loadGallery();
  }, [zoomItem, galleryPage, gallerySort])

  const formatDate = useCallback((dateStr) => {
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return dateStr
    return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium' }).format(d)
  }, [])

  const prettify = useCallback(str => str ? str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '', [])

  const submitComment = async () => {
    const text = replyToId ? replyText.trim() : newComment.trim();
    if (!text || !zoomItem) return;
    // Read user info from cookies
    const discordId = getCookie('discord_id');
    const userId = getCookie('user_id');
    const username = getCookie('username');
    const avatar = getCookie('avatar');
    const pfpUrl = discordId && avatar
      ? `https://cdn.discordapp.com/avatars/${discordId}/${avatar}.png`
      : '';
    const payload = {
      comment_text: text,
      discord_id: discordId,
      user_id: userId,
      username,
      pfp: pfpUrl,
    };
    if (replyToId) payload.parent_comment_id = replyToId;
    try {
      await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${zoomItem.item_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setReplyToId('');
      if (replyToId) {
        setReplyText('');
      } else {
        setNewComment('');
      }
      loadComments();
    } catch {
      notify('error', 'Failed to comment');
    }
  };

  // Handler for like/dislike reactions on comments
  const handleReaction = async (commentId, action) => {
    // Read current user ID from cookies for backend deduplication
    const userId = getCookie('user_id');
    try {
      await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${zoomItem.item_id}/comments`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment_id: commentId, action, user_id: userId })
      });
      loadComments();
    } catch {
      notify('error', 'Failed to update reaction');
    }
  };

  const handleScreenshot = async e => {
    const file = e.target.files?.[0];
    if (!file || !zoomItem) return;

    if (file.size > 5_000_000) {
      notify('error', 'File too large (max 5MB)');
      return;
    }

    const discordId = getCookie('discord_id');
    const userId    = getCookie('user_id');
    const username  = getCookie('username');
    const avatar    = getCookie('avatar');

    const reader = new FileReader();
    reader.onloadend = async () => {
      setUploading(true);
      try {
        // Strip data URL prefix to get pure base‑64
        const base64Data = reader.result.split(',')[1];
        const payload = {
          image_base64: base64Data,
          discord_id: discordId,
          user_id: userId,
          username,
          avatar
        };
        const res = await fetch(
          `${API_BASE}/${API_VERSION}/private/gym-class-item-category/${zoomItem.item_id}/gallary`,
          {
            method : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body   : JSON.stringify(payload)
          }
        );

        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();

        // Optimistically prepend to gallery
        setGallery(g => [{ url: data.image_url, thumb: data.image_url, ...data }, ...g]);
      } catch (err) {
        notify('error', err.message);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const QUICK_FILTER_MAP = useMemo(() => ({
    character_hair: ['character', 'hair'],
    character_facial_hair: ['character', 'facial hair'],
    clothes_base: ['clothes', 'irl'],
    clothes_jersey: ['clothes', 'jersey'],
    clothes_mascot: ['clothes', 'mascot'],
    clothes_catchers: ['clothes', 'catchers gear'],
    accessories_eyes: ['accessories', 'eyes'],
    accessories_head: ['accessories', 'head'],
    accessories_hands: ['accessories', 'hands'],
    accessories_tattoos: ['accessories', 'tattoos'],
    accessories_gadgets: ['accessories', 'gadgets'],
    tags_frames: ['tag/effects', 'frames'],
    tags_badges: ['tag/effects', 'badges'],
    tags_sounds: ['tag/effects', 'sounds']
  }), [])
  const QUICK_BUTTONS = [
    ['bundles_only','🎁'],
    ['character_hair','💇'],
    ['character_facial_hair','🧔'],
    ['clothes_base','👕'],
    ['clothes_jersey','⚾'],
    ['clothes_mascot','🐻'],
    ['clothes_catchers','🎯'],
    ['accessories_eyes','🕶️'],
    ['accessories_head','🎩'],
    ['accessories_hands','🧤'],
    ['accessories_tattoos','🔖'],
    ['accessories_gadgets','📱'],
    ['tags_frames','🖼️'],
    ['tags_badges','🏅'],
    ['tags_sounds','🎵']
  ]

  const notify = useNotification()

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchDebounce, quickFilter, advPurchaseType, advAccessType])

  useEffect(() => {
    let canceled = false
    const loadAll = async () => {
      setLoading(true)
      try {
        const collected = []
        let lastKey = ''
        const PAGE = 250
        do {
          const qs = new URLSearchParams({ limit: String(PAGE) })
          if (lastKey) qs.append('lastKey', lastKey)
          const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category?` + qs.toString(), { method: 'GET' })
          if (!res.ok) throw new Error('Failed to load')
          const data = await res.json()
          collected.push(...(data.items || []))
          lastKey = data.lastKey || ''
        } while (lastKey && !canceled)
        if (!canceled) setItems(collected)
      } catch (err) {
        if (!canceled) notify('error', err.message)
      } finally {
        if (!canceled) setLoading(false)
      }
    }
    loadAll()
    return () => { canceled = true }
  }, [notify])

  useEffect(() => {
    if (!deepLinkId) return
    let canceled = false
    const loadOne = async () => {
      try {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/${deepLinkId}`)
        if (!canceled && res.ok) {
          const data = await res.json()
          setZoomItem(data)
        }
      } catch {
      }
    }
    loadOne()
    return () => { canceled = true }
  }, [deepLinkId])

  const filtered = useMemo(() => {
    let result = items
    if (quickFilter === 'bundles_only') {
      result = result.filter(i => i.purchaseType === 'Bundle')
    }
    if (quickFilter && QUICK_FILTER_MAP[quickFilter]) {
      const [typeTerm, subTerm] = QUICK_FILTER_MAP[quickFilter]
      result = result.filter(i => {
        const t = (i.itemType || '').toLowerCase()
        const s1 = (i.subType || '').toLowerCase()
        const s2 = (i.subType2 || '').toLowerCase()
        return t.includes(typeTerm) && (s1.includes(subTerm) || s2.includes(subTerm))
      })
    }
    if (advPurchaseType) {
      result = result.filter(i => (i.purchaseType || '').toLowerCase() === advPurchaseType.toLowerCase())
    }
    if (advAccessType) {
      result = result.filter(i => (i.accessType || '').toLowerCase() === advAccessType.toLowerCase())
    }
    const term = searchDebounce.toLowerCase().trim()
    if (!term) return result
    return result.filter(i =>
        (i.productName || '').toLowerCase().includes(term) ||
        (i.itemType || '').toLowerCase().includes(term) ||
        (i.purchaseType || '').toLowerCase().includes(term)
    )
  }, [items, searchDebounce, quickFilter, advPurchaseType, advAccessType, QUICK_FILTER_MAP])

  const bundleGroups = useMemo(() => {
    const groups = {}
    filtered.forEach(i => {
      if (i.purchaseType === 'Bundle' && i.bundleName) {
        if (!groups[i.bundleName]) groups[i.bundleName] = []
        groups[i.bundleName].push(i)
      }
    })
    return Object.entries(groups)
  }, [filtered])

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filtered.slice(startIndex, startIndex + itemsPerPage)
  }, [filtered, currentPage, itemsPerPage])

  const totalPages = Math.ceil(filtered.length / itemsPerPage)

  const activeFilters = useMemo(() => {
    const filters = []
    if (searchDebounce) filters.push({ type: 'search', value: searchDebounce, label: `Search: "${searchDebounce}"` })
    if (quickFilter) {
      const [typeTerm, subTerm] = QUICK_FILTER_MAP[quickFilter]
      filters.push({ type: 'quick', value: quickFilter, label: `${typeTerm} - ${subTerm}` })
    }
    if (advPurchaseType) filters.push({ type: 'purchase', value: advPurchaseType, label: `Purchase: ${advPurchaseType}` })
    if (advAccessType) filters.push({ type: 'access', value: advAccessType, label: `Access: ${prettify(advAccessType)}` })
    return filters
  }, [searchDebounce, quickFilter, advPurchaseType, advAccessType, QUICK_FILTER_MAP, prettify])

  const clearFilter = useCallback((filterType) => {
    switch (filterType) {
      case 'search':
        setSearch('')
        break
      case 'quick':
        setQuickFilter('')
        break
      case 'purchase':
        setAdvPurchaseType('')
        break
      case 'access':
        setAdvAccessType('')
        break
      default:
        break
    }
  }, [])


  const openFileDialog = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMatchModalOpen(true);
    setMatchedItems([]);
    setMatchLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const b64 = reader.result.split(',')[1];
        const res = await fetch('https://api.ballhead.app/internal/item-category/search?k=3', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_base64: b64 })
        });
        if (!res.ok) throw new Error('Search failed');
        const { matches } = await res.json();
        // Map over matches, find corresponding item, add distance and score
        const matched = matches.map(m => {
          const item = items.find(it => it.imageUrl && it.imageUrl.endsWith(m.s3_key));
          if (!item) return null;
          const score = Math.round((1 / (1 + m.distance)) * 100);
          return { ...item, distance: m.distance, score };
        }).filter(Boolean);
        setMatchedItems(matched);
      } catch (err) {
        notify('error', err.message);
      } finally {
        setMatchLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  return (
      <>
        <Helmet>
          <title>{zoomItem ? zoomItem.productName : 'Items'}</title>
          {zoomItem && (
            <>
              <meta property="og:title" content={zoomItem.productName} key="og:title" />
              {zoomItem.description && (
                <meta
                  property="og:description"
                  content={zoomItem.description}
                  key="og:description"
                />
              )}
              <meta
                property="og:image"
                content={zoomItem.imageUrl}
                key="og:image"
              />
              <meta
                property="og:url"
                content={`${window.location.origin}/items/${zoomItem.item_id}`}
                key="og:url"
              />
              <meta property="og:type" content="article" key="og:type" />
              <meta
                name="twitter:card"
                content="summary_large_image"
                key="twitter:card"
              />
              <meta
                name="twitter:title"
                content={zoomItem.productName}
                key="twitter:title"
              />
              {zoomItem.description && (
                <meta
                  name="twitter:description"
                  content={zoomItem.description}
                  key="twitter:description"
                />
              )}
              <meta
                name="twitter:image"
                content={zoomItem.imageUrl}
                key="twitter:image"
              />
            </>
          )}
        </Helmet>
        <div className="items-page">
        {loading && <GlobalThrobberComponent />}
        <div className="search-bar">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items" className="item-search" />
          <div className="quick-filters">
            {QUICK_BUTTONS.map(([key, icon]) => (
                <button
                    key={key}
                    className={`filter-button ${quickFilter === key ? 'active' : ''}`}
                    onClick={() => setQuickFilter(quickFilter === key ? '' : key)}
                    title={key === 'bundles_only' ? 'Bundles' : (QUICK_FILTER_MAP[key] ? `${QUICK_FILTER_MAP[key][0]} - ${QUICK_FILTER_MAP[key][1]}` : '')}
                >
                  {icon}
                </button>
            ))}
          </div>
          <button className="filter-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>⚙️</button>
          {activeFilters.length > 0 && (
              <button
                  className="clear-all-filters"
                  onClick={() => { setSearch(''); setQuickFilter(''); setAdvPurchaseType(''); setAdvAccessType('') }}
              >
                Clear All
              </button>
          )}
          <button className="screenshot-match-button" onClick={openFileDialog}>
            {matchLoading ? 'Matching…' : 'Screenshot Match'}
          </button>
          <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
          />
        </div>
        <div className={`advanced-filters ${showAdvanced ? 'open' : ''}`}>
          <select value={advPurchaseType} onChange={e => setAdvPurchaseType(e.target.value)}>
            {PURCHASE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt || 'Any Purchase Type'}</option>)}
          </select>
          <select value={advAccessType} onChange={e => setAdvAccessType(e.target.value)}>
            {ACCESS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt ? prettify(opt) : 'Any Access Type'}</option>)}
          </select>
        </div>
        {activeFilters.length > 0 && (
            <div className="active-filters">
              <span className="active-filters-label">Active filters:</span>
              <div className="filter-chips">
                {activeFilters.map((filter, index) => (
                    <div key={index} className="filter-chip">
                      <span className="filter-chip-label">{filter.label}</span>
                      <button
                          className="filter-chip-remove"
                          onClick={() => clearFilter(filter.type)}
                      >
                        ✕
                      </button>
                    </div>
                ))}
              </div>
            </div>
        )}
        {bundleGroups.length > 0 && (
            <div className="bundles-rail">
              {bundleGroups.map(([name, items]) => (
                  <div key={name} className="bundle-card">
                    <div
                        className="bundle-hero"
                        onClick={() => setSelectedBundle({ name, items })}
                    >
                      <img src={items[0].imageUrl} alt={name} />
                      <div className="bundle-info">
                        <div className="bundle-name">{name}</div>
                        <div className="bundle-count">{items.length} items · {items[0].bundlePrice} coins</div>
                      </div>
                    </div>
                    {expandedBundles[name] && (
                        <div className="bundle-items-grid">
                          {items.map(item => (
                              <div key={item.item_id} className="bundle-item-card">
                                <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                                <div className="bundle-item-name">{item.productName}</div>
                              </div>
                          ))}
                        </div>
                    )}
                  </div>
              ))}
            </div>
        )}
        <div className="items-grid">
          {paginatedItems.map(item => (
              <div key={item.id} className="item-card" onClick={() => setZoomItem(item)}>
                <div className="item-media" onClick={e => e.stopPropagation()}>
                  {(() => {
                    const media = []
                    if (item.imageUrl) media.push({ type: 'image', url: item.imageUrl })
                    if (item.videoUrl) media.push({ type: 'video', url: item.videoUrl })
                    return <CarouselComponent mediaItems={media} productName={item.productName} />
                  })()}
                </div>
                <div className="item-details">
                  <h4 className="item-name">{item.productName}</h4>
                  <div className="item-info">
                    <div className="item-info-group">
                      <div className="item-info-label">Type</div>
                      <span className={`item-type ${(item.itemType || '').toLowerCase()}`}>{item.itemType}{item.subType && <span className="sub-type"> - {item.subType}</span>}{item.subType2 && <span className="sub-type2"> - {item.subType2}</span>}</span>
                    </div>
                    <div className="item-info-group">
                      <div className="item-info-label">Purchase</div>
                      <span className={`purchase-type ${(item.purchaseType || '').toLowerCase()}`}>{item.purchaseType}</span>
                    </div>
                  </div>
                  <div className="item-details-row">
                    <div className="price-section">
                      <div className="item-info-label">Price</div>
                      <span className="item-price">{item.purchaseType === 'Bundle' ? (item.bundlePriceType === 'Both' ? `${item.bundleCoinPrice} coins / ${item.bundleDollarPrice}` : item.bundlePrice) : item.price ? `${item.price} ${item.purchaseType === 'Purchase' ? 'USD' : 'coins'}` : 'Free'}</span>
                    </div>
                    <div className="access-section">
                      <div className="item-info-label">Access</div>
                      <span className={`access-type ${(item.accessType || '').toLowerCase()}`}>{prettify(item.accessType)}</span>
                    </div>
                  </div>
                  {item.seasonPass !== 'None' && <div className="item-season-pass"><div className="season-pass-label">Season Pass</div><div>{item.seasonPass} - {item.seasonPassName}</div></div>}
                  <div className="item-status">
                    <div className="status-pair">
                      <div className="status-label">Availability</div>
                      <span className={`availability ${item.isUnlockable ? 'available' : 'unavailable'}`}>{item.isUnlockable ? 'Available' : 'Unavailable'}</span>
                    </div>
                    <div className="status-pair">
                      <div className="status-label">Released</div>
                      <span className="release-date">{formatDate(item.released)}</span>
                    </div>
                  </div>
                </div>
              </div>
          ))}
        </div>
        {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                  className="page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                if (
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 2
                ) {
                  return (
                      <button
                          key={page}
                          className={`page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                  )
                }
                if (
                    (page === 2 && currentPage > 4) ||
                    (page === totalPages - 1 && currentPage < totalPages - 3)
                ) {
                  return <span key={page} className="page-ellipsis">…</span>
                }
                return null
              })}
              <button
                  className="page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                ›
              </button>
            </div>
        )}
        {selectedBundle && (
            <div className="bundle-modal">
              <div className="bundle-modal-backdrop" onClick={() => setSelectedBundle(null)} />
              <div className="bundle-modal-content">
                <button className="bundle-modal-close" onClick={() => setSelectedBundle(null)}>✕</button>
                <h3 className="bundle-modal-title">{selectedBundle.name}</h3>
                <div className="bundle-modal-grid">
                  {selectedBundle.items.map(item => (
                      <div key={item.item_id} className="bundle-modal-item">
                        <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                        <div className="bundle-modal-item-info">
                          <div className="bundle-modal-item-name">{item.productName}</div>
                          <div className="bundle-modal-item-type">{item.itemType} {item.subType && `- ${item.subType}`}{item.subType2 && ` - ${item.subType2}`}</div>
                          <div className="bundle-modal-item-price">{item.bundlePrice ? `${item.bundlePrice} coins` : 'Included'}</div>
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            </div>
        )}
        {matchModalOpen && (
            <div className="bundle-modal">
              <div className="bundle-modal-backdrop" onClick={() => setMatchModalOpen(false)} />
              <div className="bundle-modal-content">
                <button className="bundle-modal-close" onClick={() => setMatchModalOpen(false)}>✕</button>
                <h3 className="bundle-modal-title">Visual Matches</h3>
                {matchLoading ? (
                  <div className="match-loading">Matching image, please wait...</div>
                ) : (
                  <>
                    <p className="match-disclaimer">This feature is experimental and still learning. Results will improve over time.</p>
                    <div className="bundle-modal-grid">
                      {matchedItems.map(item => (
                        <div key={item.item_id} className="bundle-modal-item">
                          <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                          <div className="bundle-modal-item-info">
                            <div className="bundle-modal-item-name">{item.productName}</div>
                            <div className="match-score">{item.score}% match</div>
                            <div className="bundle-modal-item-type">{item.itemType} {item.subType && `- ${item.subType}`}{item.subType2 && ` - ${item.subType2}`}</div>
                          </div>
                        </div>
                      ))}
                      {!matchLoading && matchedItems.length === 0 && <div>No matches found.</div>}
                    </div>
                  </>
                )}
              </div>
            </div>
        )}
        <AnimatePresence>
          {zoomItem && (
              <motion.div className="bundle-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="bundle-modal-backdrop" onClick={() => setZoomItem(null)} />
                <motion.div className="zoom-modal-content" initial={{ scale: 0.8 }} animate={{ scale: 1 }} exit={{ scale: 0.8 }}>
                  {uploading && <GlobalThrobberComponent />}
                  <button className="bundle-modal-close" aria-label="Close" onClick={() => setZoomItem(null)}>✕</button>
                  <button className="zoom-share-button" onClick={() => {
                    const url = `${window.location.origin}/items/${zoomItem.item_id}`
                    navigator.clipboard.writeText(url)
                    notify('success', 'Link copied')
                  }}>🔗</button>
                  <div className="zoom-item-layout">
                    <div className="zoom-left">
                      <div className="item-media">
                        <CarouselComponent mediaItems={[{ type: 'image', url: zoomItem.imageUrl }]} productName={zoomItem.productName} />
                      </div>
                    </div>
                    <div className="zoom-right">
                      <div className="item-details">
                        <h4 className="item-name">{zoomItem.productName}</h4>
                        <div className="item-info">
                          <div className="item-info-group">
                            <div className="item-info-label">Type</div>
                            <span className={`item-type ${(zoomItem.itemType || '').toLowerCase()}`}>{zoomItem.itemType}{zoomItem.subType && <span className="sub-type"> - {zoomItem.subType}</span>}{zoomItem.subType2 && <span className="sub-type2"> - {zoomItem.subType2}</span>}</span>
                          </div>
                          <div className="item-info-group">
                            <div className="item-info-label">Purchase</div>
                            <span className={`purchase-type ${(zoomItem.purchaseType || '').toLowerCase()}`}>{zoomItem.purchaseType}</span>
                          </div>
                        </div>
                        <div className="item-details-row">
                          <div className="price-section">
                            <div className="item-info-label">Price</div>
                            <span className="item-price">{zoomItem.purchaseType === 'Bundle' ? (zoomItem.bundlePriceType === 'Both' ? `${zoomItem.bundleCoinPrice} coins / ${zoomItem.bundleDollarPrice}` : zoomItem.bundlePrice) : zoomItem.price ? `${zoomItem.price} ${zoomItem.purchaseType === 'Purchase' ? 'USD' : 'coins'}` : 'Free'}</span>
                          </div>
                          <div className="access-section">
                            <div className="item-info-label">Access</div>
                            <span className={`access-type ${(zoomItem.accessType || '').toLowerCase()}`}>{prettify(zoomItem.accessType)}</span>
                          </div>
                        </div>
                        {zoomItem.seasonPass !== 'None' && <div className="item-season-pass"><div className="season-pass-label">Season Pass</div><div>{zoomItem.seasonPass} - {zoomItem.seasonPassName}</div></div>}
                        <div className="item-status">
                          <div className="status-pair">
                            <div className="status-label">Availability</div>
                            <span className={`availability ${zoomItem.isUnlockable ? 'available' : 'unavailable'}`}>{zoomItem.isUnlockable ? 'Available' : 'Unavailable'}</span>
                          </div>
                          <div className="status-pair">
                            <div className="status-label">Released</div>
                            <span className="release-date">{formatDate(zoomItem.released)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="zoom-comments-section">
                        <div className="zoom-section-header">
                          <div className="zoom-section-title">
                            💬 Comments
                            {comments.length > 0 && <span className="zoom-section-count">{comments.length}</span>}
                          </div>
                        </div>
                        <div className="zoom-comment-list">
                          {renderComments(comments)}
                        </div>
                        {loggedIn ? (
                          <div className="zoom-comment-form">
                            <textarea
                              value={newComment}
                              onChange={e => setNewComment(e.target.value)}
                              placeholder="Share your thoughts about this item..."
                            />
                            <button onClick={submitComment}>Post</button>
                          </div>
                        ) : (
                          <div className="zoom-comment-login">
                            Please sign in to post comments.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="zoom-gallery-section">
                    <div className="zoom-section-header">
                      <div className="zoom-section-title">
                        🖼️ Community Screenshots
                        {gallery.length > 0 && <span className="zoom-section-count">{gallery.length}</span>}
                      </div>
                    </div>
                    <div className="zoom-gallery-controls">
                      <select value={gallerySort} onChange={e => { setGalleryPage(1); setGallerySort(e.target.value) }}>
                        <option value="date">Newest</option>
                        <option value="popular">Popular</option>
                        <option value="user">User</option>
                      </select>
                      {loggedIn ? (
                        <label className="zoom-gallery-upload-btn">
                          Upload Screenshot
                          <input type="file" accept="image/*" onChange={handleScreenshot} />
                        </label>
                      ) : (
                        <div className="zoom-gallery-login">
                          Please sign in to upload screenshots.
                        </div>
                      )}
                    </div>
                    <div className="zoom-gallery">
                      {gallery.map((g, i) => <img key={g.id || i} src={g.thumb || g.url} alt="screenshot" />)}
                    </div>
                    {galleryHasMore && <button className="zoom-gallery-load" onClick={() => setGalleryPage(p => p + 1)}>Load More Screenshots</button>}
                  </div>
                </motion.div>
              </motion.div>
          )}
        </AnimatePresence>
      </div>
      </>
  )
}
